import React from 'react';
import { MapPin, Home, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from './ui/card';

const About = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: MapPin,
      title: t('location'),
      description: t('locationDesc'),
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Home,
      title: t('comfort'),
      description: t('comfortDesc'),
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: Heart,
      title: t('peaceful'),
      description: t('peacefulDesc'),
      color: 'from-red-500 to-pink-500'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-orange-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="h-px w-12 bg-orange-400" />
              <div className="w-2 h-2 rotate-45 bg-orange-400" />
              <div className="h-px w-12 bg-orange-400" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              {t('aboutTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('aboutDescription')}
            </p>
          </div>

          {/* Features Grid */}
          <div className="mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8">
              {t('whyChoose')}
            </h3>
            
            {/* Security Badge */}
            <div className="bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-200 rounded-xl p-6 mb-8 text-center max-w-2xl mx-auto">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-gray-800">
                  {t('language') === 'bn' ? 'নিরাপত্তা প্রথম' : 'Safety First'}
                </h4>
              </div>
              <p className="text-gray-700 font-medium">
                {t('language') === 'bn' 
                  ? 'একক মহিলা এবং শিশুদের জন্য নিরাপদ স্থান - পরিবার এখানে থাকে' 
                  : 'Secure place for single ladies and kids - Family lives here'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card 
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white overflow-hidden group"
                >
                  <CardContent className="p-8 text-center">
                    <div className={`w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-800 mb-3">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Image Section */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl group">
              <img
                src="https://images.unsplash.com/photo-1755757573547-294f87d1c16b?w=800&q=80"
                alt="Vrindavan Temple"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                <p className="text-white font-semibold text-lg">Prem Mandir, Vrindavan</p>
              </div>
            </div>
            <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl group">
              <img
                src="https://images.unsplash.com/photo-1676574834058-1d5717a3c04a?w=800&q=80"
                alt="Vrindavan Evening"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                <p className="text-white font-semibold text-lg">Evening Aarti</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
